import { Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { AuthStore } from './auth/auth.store';

const PENDING_KEY = 'lance.deepLinkPending';
const SCHEME = 'lance-x://';

/**
 * Normalizes push notification routes to canonical frontend routes based on role.
 * Maps legacy paths like `/candidaturas/:id` or generic `/carteira` to appropriate targets.
 */
export function normalizePushRoute(route: string, userRole?: string | null): string {
  if (!route) return '/';

  // Strip scheme or host if inadvertently present
  let cleanRoute = route;
  if (cleanRoute.startsWith(SCHEME)) {
    cleanRoute = '/' + cleanRoute.slice(SCHEME.length);
  }

  if (cleanRoute.startsWith('/candidaturas/')) {
    const id = cleanRoute.slice('/candidaturas/'.length);
    return id ? `/profissional/meus-eventos/${id}` : '/profissional/meus-eventos';
  }

  if (cleanRoute.startsWith('/eventos/')) {
    const id = cleanRoute.slice('/eventos/'.length);
    if (userRole === 'EMPRESA') {
      return id ? `/empresa/eventos/${id}` : '/empresa/eventos';
    }
    return id ? `/profissional/eventos/${id}` : '/profissional/feed';
  }

  if (cleanRoute === '/carteira') {
    return userRole === 'EMPRESA' ? '/empresa/carteira' : '/profissional/carteira';
  }

  return cleanRoute;
}

/**
 * Deep link routing:
 *   lance-x://eventos/:id                       → /profissional/eventos/:id
 *   lance-x://candidaturas/:id                  → /profissional/meus-eventos/:id
 *   lance-x://empresa/eventos/:id               → /empresa/eventos/:id
 *   lance-x://empresa/eventos/:id/candidatos    → /empresa/eventos/:id/candidatos
 *
 * Auth-gated: stashes target em sessionStorage se user não autenticado; consumido pós-login.
 * Wraps router.navigate em NgZone.run para evitar change-detection skip do listener nativo.
 */
@Injectable({ providedIn: 'root' })
export class DeepLinkService {
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly authStore = inject(AuthStore);

  async init(): Promise<void> {
    // Bridge from the FCM Web Push service worker — the SW's notificationclick
    // handler postMessages {type:'lance:push-deeplink', deepLink}. We listen on
    // both `serviceWorker.message` (when the SW is controlling this page) and
    // a window-level CustomEvent as a redundant fallback. Phase 15 (PUSH-03).
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      try {
        navigator.serviceWorker.addEventListener('message', (ev: MessageEvent) => {
          const msg = ev.data as { type?: string; deepLink?: string } | undefined;
          if (msg?.type === 'lance:push-deeplink' && typeof msg.deepLink === 'string') {
            this.handleDirectRoute(msg.deepLink);
          }
        });
      } catch {
        // ignore
      }
    }

    if (!Capacitor.isNativePlatform()) return;
    try {
      await App.addListener('appUrlOpen', (event) => {
        this.handle(event.url);
      });
    } catch {
      // Web platform — App plugin may throw; ignore.
    }
  }

  /**
   * Phase 15 (PUSH-03 / T-15-10) — direct-route variant for push payloads
   * whose `deepLink` is already an Angular route (NOT a custom-scheme URL).
   * Validates the same way as the lance-x:// branch: must be a path-only string
   * starting with `/` and never containing `://`.
   */
  private handleDirectRoute(route: string): void {
    if (!route.startsWith('/') || route.startsWith('//') || route.includes('://')) {
      return;
    }
    const role = this.authStore.user()?.role ?? null;
    const normalized = normalizePushRoute(route, role);
    const authed = !!this.authStore.accessToken();
    this.zone.run(() => {
      if (authed) {
        void this.router.navigateByUrl(normalized);
      } else {
        this.stashPending(normalized);
        void this.router.navigateByUrl('/login?redirect=' + encodeURIComponent(normalized));
      }
    });
  }

  /** Consume pending deep link após login. Called from auth success path. */
  consumePending(): void {
    const target = this.readPending();
    if (target) {
      this.clearPending();
      this.zone.run(() => void this.router.navigateByUrl(target));
    }
  }

  private handle(url: string): void {
    if (!url?.startsWith(SCHEME)) return;
    const path = url.slice(SCHEME.length); // e.g. "eventos/abc123"
    const route = this.resolveRoute(path);
    if (!route) return;
    const authed = !!this.authStore.accessToken();
    this.zone.run(() => {
      if (authed) {
        void this.router.navigateByUrl(route);
      } else {
        this.stashPending(route);
        void this.router.navigateByUrl('/login?redirect=' + encodeURIComponent(route));
      }
    });
  }

  private resolveRoute(path: string): string | null {
    // Empresa prefix must be checked BEFORE the generic eventos/ branch.
    if (path.startsWith('empresa/eventos/')) {
      const tail = path.slice('empresa/eventos/'.length);
      if (!tail) return null;
      const [id, sub] = tail.split('/');
      if (!id) return null;
      if (sub === 'candidatos') return `/empresa/eventos/${id}/candidatos`;
      if (!sub) return `/empresa/eventos/${id}`;
      return null;
    }
    if (path.startsWith('eventos/')) {
      const id = path.slice('eventos/'.length);
      return id ? `/profissional/eventos/${id}` : null;
    }
    if (path.startsWith('candidaturas/')) {
      const id = path.slice('candidaturas/'.length);
      return id ? `/profissional/meus-eventos/${id}` : null;
    }
    return null;
  }

  private stashPending(route: string): void {
    try {
      sessionStorage.setItem(PENDING_KEY, route);
    } catch {
      // Storage unavailable — silently drop.
    }
  }

  private readPending(): string | null {
    try {
      return sessionStorage.getItem(PENDING_KEY);
    } catch {
      return null;
    }
  }

  private clearPending(): void {
    try {
      sessionStorage.removeItem(PENDING_KEY);
    } catch {
      // ignore
    }
  }
}
